import * as React from 'react';
import { useEffect, useState } from 'react';
import BayPicker from '../../../components/releasing/BayPicker';
import Button from '../../../lib/bootstrap-ui/Button';
import DropdownOption from '../../../lib/bootstrap-ui/Dropdown/DropdownOption';
import SelectDropdown from '../../../lib/bootstrap-ui/Dropdown/SelectDropdown';
import Modal, {
    ModalBody,
    ModalFooter,
    ModalHeader
} from '../../../lib/bootstrap-ui/Modal';
import Bay from '../../../models/bays/Bay';
import { Load } from '../../../models/loads/Load';
import { SMSTemplate } from '../../../models/smsTemplates/SMSTemplate';
import { mapBaysToDropdownOptions } from '../../../services/bayService';
import PhoneNumberInput from '../../common/PhoneNumberInput';

interface LoadSmsModalProps {
    load: Load;
    bays: Bay[];
    loadingBays: boolean;
    smsTemplates: SMSTemplate[];
    showModal: boolean;
    onSave(
        contactNumber: string,
        smsTemplateId: string,
        bayNumber: number,
        bayNumberUpdateable: boolean
    ): void;
    onClose(): void;
}

const mapSMSTemplatesToDropdownOptions = (
    smsTemplates: SMSTemplate[] | null
): DropdownOption[] => {
    const smsTemplateData = smsTemplates;
    return (
        smsTemplateData?.map(s => ({
            label: s.name,
            value: s.id
        })) || []
    );
};

const getDefaultSMSTemplate = (smsTemplates: SMSTemplate[]) => {
    return smsTemplates.find(t => t.isDefault);
};

const LoadSmsModal: React.FC<LoadSmsModalProps> = ({
    load,
    bays,
    loadingBays,
    smsTemplates,
    showModal,
    onSave,
    onClose
}) => {
    const [
        selectedSMSTemplate,
        setSelectedSMSTemplate
    ] = useState<SMSTemplate | null>(
        getDefaultSMSTemplate(smsTemplates) ?? null
    );
    const [loadBayNumber, setLoadBayNumber] = useState(load.bayOrPagerNumber);
    const [loadContactNumber, setLoadContactNumber] = useState(
        load.contactNumber
    );
    const [contactNumberIsValid, setContactNumberIsValid] = useState(false);

    useEffect(() => {
        setSelectedSMSTemplate(getDefaultSMSTemplate(smsTemplates) ?? null);
    }, [smsTemplates]);

    const handleSave = () => {
        if (
            loadContactNumber &&
            selectedSMSTemplate &&
            selectedSMSTemplate.id
        ) {
            onSave(
                loadContactNumber,
                selectedSMSTemplate.id,
                loadBayNumber,
                selectedSMSTemplate.allowBaySelection ?? false
            );
        }
        setLoadContactNumber(null);
        setLoadBayNumber(0);
    };

    const handleBayChanged = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const bayNumber = parseInt(event.target.value, 10);
        setLoadBayNumber(bayNumber);
    };

    const handleSelectedSmsTemplateChanges = (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => {
        const smsTemplate =
            smsTemplates.find(t => t.id === event.target.value) ?? null;
        setSelectedSMSTemplate(smsTemplate);

        smsTemplate?.allowBaySelection
            ? setLoadBayNumber(load.bayOrPagerNumber)
            : setLoadBayNumber(0);
    };

    const getValueAsStringOrNull = (value: number | null) => {
        if (value !== null) {
            return String(value);
        }
        return null;
    };

    const bayNumberUpdateable =
        (load.status === 'Ready to Release' ||
            load.status === 'Awaiting Bay') &&
        selectedSMSTemplate?.allowBaySelection &&
        load.isThirdParty;
    const sendSmsAllowed =
        selectedSMSTemplate &&
        loadContactNumber &&
        ((bayNumberUpdateable && loadBayNumber) || !bayNumberUpdateable);

    return (
        <Modal isOpen={showModal} onRequestClose={onClose}>
            <ModalHeader>
                <h5>Contact driver: {load.loadName}</h5>
            </ModalHeader>
            <ModalBody>
                <PhoneNumberInput
                    label={'Contact Number:'}
                    placeholder={'Contact Number...'}
                    onPhoneNumberChanged={setLoadContactNumber}
                    phoneNumber={loadContactNumber}
                    required
                    onPhoneNumberValidation={setContactNumberIsValid}
                />
                <SelectDropdown
                    id={'SmsTemplateDropdown'}
                    label={'Message:'}
                    dropdownOptions={mapSMSTemplatesToDropdownOptions(
                        smsTemplates
                    )}
                    defaultText={'Select...'}
                    onChange={handleSelectedSmsTemplateChanges}
                    selectedOption={selectedSMSTemplate?.id ?? null}
                    hideBottomPadding
                />
                {bayNumberUpdateable && (
                    <BayPicker
                        bayDropDownOptions={
                            mapBaysToDropdownOptions(bays) || []
                        }
                        disabled={loadingBays}
                        loadingBays={loadingBays}
                        selectedOption={getValueAsStringOrNull(loadBayNumber)}
                        onBayChange={handleBayChanged}
                        hideBottomPadding
                    />
                )}
            </ModalBody>
            <ModalFooter>
                <Button styleType="secondary" onClick={onClose}>
                    Close
                </Button>
                <Button
                    styleType="primary"
                    disabled={!sendSmsAllowed || !contactNumberIsValid}
                    onClick={handleSave}
                >
                    Send
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default LoadSmsModal;
